import {
  Paper, Text, TextProps, Tooltip,
} from "@mantine/core";
import { useClipboard, useHover } from "@mantine/hooks";
import { useEffect, useState, useCallback } from "react";

interface TruncatedTextProps extends TextProps {
  width: string | number;
  tooltipText?: string; // Optional custom tooltip text, defaults to children content
  className?: string;
  copy?: boolean;
  children: React.ReactNode;
}

const TruncatedTextTooltip = ({
  width,
  tooltipText,
  className = "",
  children,
  copy = false,
  ...otherProps
}: TruncatedTextProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const { hovered, ref } = useHover();
  const clipboard = useClipboard({ timeout: 1000 });
  const contentAsString = typeof children === 'string' ? children : String(children ?? '');

  // Determine what text to show in the tooltip
  const tooltipContent = tooltipText || contentAsString;

  const checkTruncation = useCallback(() => {
    if (ref.current) {
      setIsTruncated(ref.current.offsetWidth < ref.current.scrollWidth);
    }
  }, [ref]);

  useEffect(() => {
    checkTruncation();
    
    // Check truncation on resize
    window.addEventListener('resize', checkTruncation);
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [checkTruncation, children, width]);

  return (
    <Paper
    shadow="none"
    maw={width}
      className={`overflow-hidden text-ellipsis text-inherit whitespace-nowrap bg-inherit ${className}`}
    >
      <Tooltip
        withinPortal
        transitionProps={{ transition: 'skew-up', duration: 300 }}
        opened={clipboard.copied || (hovered && isTruncated)}
        label={clipboard.copied ? 'Copied!' : (isTruncated ? tooltipContent : "")}
        position="top"
        multiline
        maw={300}
      >
        <Text
          truncate
          ref={ref}
          size={otherProps.size}
          fw={otherProps.fw}
          c={otherProps.c}
          ta={otherProps.ta}
          classNames={otherProps.classNames}
          style={copy ? { cursor: 'pointer' } : undefined}
          onClick={() => copy && clipboard.copy(contentAsString)}
        >
          {children}
        </Text>
      </Tooltip>
    </Paper>
  );
};

export default TruncatedTextTooltip;
